import { useEffect, useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { Content, Server } from '@wajeez-web/core/api-base';
import {
  useContentSelector,
  VoiceActorsActions,
} from '@wajeez-web/stores/content';
import { useAppDispatch } from '@wajeez-web/stores/base';
import { PlusIcon } from '@heroicons/react/solid';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { useTranslation } from 'react-i18next';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { CheckBoxType } from '@wajeez-web/components/mui';
import { DataTableWrapper } from '@wajeez-web/components/helpers';
import VoiceActorModal from '../components/VoiceActorModal';

interface VoiceActorDataTableExtended extends Server.TableOfVoiceActorColumns {
  totalCount: number | undefined;
}

const VoiceActorsPage = () => {
  const [isVoiceActorModalOpen, setIsVoiceActorModalOpen] = useState(false);
  const [selectedVoiceActorId, setSelectedVoiceActorId] = useState<
    string | undefined
  >(undefined);

  const {
    tablePreferances,
    setTablePreferences,
    getData,
  } = HandleTableData(
    'voiceActors',
    VoiceActorsActions.getVoiceActorsDatatableAsync
  );

  const voiceActorsDatatable = useContentSelector(
    (state) => state.voiceActors.datatable
  );

  const voiceActorsDatatableData = voiceActorsDatatable.data;

  const dataTableDataExtended =
    voiceActorsDatatableData as VoiceActorDataTableExtended;


  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={`Voice Actors`}
        withoutRightPadding={false}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add New Voice Actor'}
                Icon={PlusIcon}
                onClick={() => {
                  setIsVoiceActorModalOpen(true);
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [AdminSystemPermissions.voiceActors.add],
          },
        ]}
        breadCrumbsLinks={[{ title: 'Voice Actors' }]}
      >
        <DataTableWrapper
          data={dataTableDataExtended as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={voiceActorsDatatable.status === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Edit',
                permission: AdminSystemPermissions.voiceActors.edit,
                endpoint: '',
                type: 'request',
                action: () => {
                  setSelectedVoiceActorId(row[1] as string);
                  setIsVoiceActorModalOpen(true);
                },
              },
            ];
          }}
          columnsSelectors={(r) => {
            return {
              '2': (
                <div
                  className="text-[#38A7B4] font-medium cursor-pointer"
                  onClick={() => {
                    setSelectedVoiceActorId(r[1] as string);
                    setIsVoiceActorModalOpen(true);
                  }}
                >
                  {r[2]}
                </div>
              ),
            };
          }}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      <VoiceActorModal
        isOpen={isVoiceActorModalOpen}
        onClose={() => {
          setIsVoiceActorModalOpen(false);
          setSelectedVoiceActorId(undefined);
        }}
        onSubmit={() => {
          getData();
          setIsVoiceActorModalOpen(false);
          setSelectedVoiceActorId(undefined);
        }}
        id={selectedVoiceActorId}
      />
    </div>
  );
};

export default VoiceActorsPage;
